import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { pluralise } from '@/lib/format';
import {
  TARGET_ALREADY_EXISTS,
  TARGET_METRICS,
  baseCurrencyFor,
  billingRegionsFor,
  describeTargetPeriod,
  formatTarget,
  isTargetAmount,
  metricIsMoney,
  targetMetricLabel,
} from '@/lib/targets';
import type {
  Target,
  TargetCreateInput,
  TargetMetric,
  TargetPeriodRecord,
  TargetUpdateInput,
  User,
} from '@/lib/types';
import {
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/* -------------------------------------------------------------------------
 * One person's sales targets.
 *
 * ## Why a modal and not a page
 *
 * There is **no `/users/:id` route in this app and no `getUser` in `api.ts`** —
 * `/users` is index-only, deliberately — so anything per-user has to be a modal
 * or an inline section. A row action opening this dialog is the only shape that
 * does not require inventing a detail route, and a target set is small enough
 * that a whole page would be mostly whitespace. `UsersPage` mounts it.
 *
 * ## The contract
 *
 *   GET    /targets  ?subjectType &subjectId &metric &periodId &page &perPage
 *                    &sort=periodStart|targetAmount|createdAt &order=asc|desc
 *   POST   /targets              201 + Location
 *   PATCH  /targets/:id          at least one field; {} is a 422
 *   DELETE /targets/:id          204
 *   GET    /target-periods       the windows
 *
 * **One row per `(subject, metric, period)`** — a person with two metrics in one
 * period has two rows, not one wide row. A wide row was proposed and rejected by
 * the backend because a CHECK constraint cannot express "these columns need a
 * currency and that one must not" across it, which is the whole reason the
 * currency rule below is enforceable at all.
 *
 * ## The five things that bite
 *
 * 1. ⚠️ **`?subjectId=` is not an ordinary filter.** Naming somebody else
 *    without the audit grant is a **403**, not a narrowed list — the same shape
 *    as `?assignedToId=` on `/tasks`. This dialog therefore NEVER retries
 *    without the key: falling back would hand the reader their own rows under
 *    another person's name, and nothing on the screen would look wrong. The
 *    refusal is a first-class state (`forbidden`) with its own copy.
 * 2. ⚠️ **`f_target_periods` shipped EMPTY**, so the day-one empty state is "no
 *    target periods are available", NOT "no targets set". Only one of those
 *    sentences tells the reader why they cannot proceed; the other invites them
 *    to press a button that cannot work. The two states are rendered separately
 *    below and must stay separate.
 *
 *    ✅ **There IS somewhere to send them now.** Periods are created and retired
 *    on `/catalog` (`TargetPeriodsCard`), so the copy names that screen instead
 *    of saying "ask an administrator" — which was true and unactionable.
 *
 *    ⚠️ The periods are fetched WITHOUT `isActive` and split at render, the same
 *    rule the deal pickers follow. Two reasons, and both bite: a RETIRED period
 *    is a 422 on `periodId` at write time (`assertPeriodUsable`), so only active
 *    ones may be offered — and filtering server-side would make "none exists"
 *    and "every one is retired" arrive as the identical empty list, which are
 *    two different problems with two different next steps.
 * 3. ⚠️ **`targetAmount` is a decimal string even for the count** (`"12.00"`).
 *    Nothing here calls `Number()` on it — `formatDecimalString`, via
 *    `formatTarget`, groups it as text.
 * 4. ⚠️ **`metric` and `baseCurrency` are a PAIR on a PATCH.** The service
 *    validates the post-merge state and refuses the contradiction rather than
 *    auto-clearing, so a money target becoming a count needs
 *    `{metric: 'won_deals', baseCurrency: null}` in ONE body. `buildPatch`
 *    emits the pair structurally whenever the metric moves — never as two
 *    independent diffs, which is what a per-key loop would produce.
 * 5. ⚠️ **No attainment bar and no percentage — and the REASON has changed.**
 *    The currency question is ANSWERED (one base currency per target, compare
 *    like with like, no conversion ever), and migration 091 widened the unique
 *    key to include the currency, so **one person may legitimately hold a GBP
 *    target and a USD target for the same metric and period** — that is ordinary,
 *    not a clash, and the 409 copy below says so. What blocks the bar now is that
 *    **nothing computes the actuals**: there is no query for them in the targets
 *    module, its repository, or anywhere else, and `/targets` serves no
 *    `/summary`. A bar drawn against a client-side sum would be this app
 *    inventing the definition of `booked`. See `src/lib/targets.ts`.
 *
 * ## Gating
 *
 * `/targets` carries **no `requireRole`**; the backend gates it on permission
 * sets with a scope, the way `/tasks` and `/notes` are gated. There is no
 * `targets` slug in this app's `ModuleSlug` union, and inventing one would make
 * `enforcedByRole` answer a question nobody has asked the server. So the
 * controls **render and the 403 is handled** — the house rule for exactly this
 * case: a control wrongly shown costs one refusal the user can read, a control
 * wrongly hidden is a working feature nobody can find.
 * --------------------------------------------------------------------- */

export interface UserTargetsModalProps {
  open: boolean;
  /** `null` renders nothing — the caller may leave the prop wired while closed. */
  user: User | null;
  /** The signed-in account's id, so "your own targets" can be worded as such. */
  currentUserId: number | null;
  onClose: () => void;
}

/**
 * Well under any plausible ceiling. The verbatim contract lists `perPage`
 * without bounds; every other list on this API maxes at 100 or 200, so 50 is
 * safe on both, and `meta` is read below rather than assumed to cover the set.
 */
const TARGETS_PAGE_SIZE = 50;

/** The whole 164-row master in one request — its ceiling is 250, not 200. */
const CURRENCY_PAGE_SIZE = 250;

/**
 * `/target-periods` has its own ceiling again: 1..200, default 50.
 *
 * Not the currencies' 250 directly above — asking for that here is a 422 naming
 * `perPage` — and not the entities' 100. Three ceilings in one file is why each
 * is a named constant with the number written down rather than a literal reused
 * by eye.
 */
const PERIODS_PAGE_SIZE = 200;

const SKELETON_ROWS = 3;

/** Uppercased before the test, so a typed "gbp" is accepted rather than scolded. */
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

/**
 * The `billingRegions` to SEND for a subject type — the plural counterpart of
 * `billingRegionFor` in `@/lib/targets`, and **the DECISION is still that
 * module's**: `subjectTypeCarriesBillingRegion` is what says which subject types
 * carry a band, and this only owns the arity.
 *
 * ⚠️ **On this form it can only ever answer `null`, and that is the point.**
 * `subjectType` is written in the same body as this value and the server checks
 * the POST-MERGE state, refusing the contradiction rather than auto-clearing —
 * so a personal target that carried a band would be a 422 naming a field this
 * screen has no control for. Reaching the answer through the module that decides
 * it, rather than writing a bare `null`, is what keeps a second opinion about
 * which subjects carry a band out of this file.
 *
 * ⛔ **NOTHING IS INVENTED FROM AN EMPTY SET: it answers `null` and lets the
 * server refuse.** That matters on the TEAM form rather than here, and the rule
 * lives with the helper so the two cannot diverge.
 *
 * ✅ **IT LIVES IN `@/lib/targets` NOW** — imported rather than declared here,
 * with the empty-set rule carried in rather than lost. ⚠️ *SUPERSEDED: "It is
 * copied in `GroupTargetsCard` and belongs in `@/lib/targets` … recorded as a
 * follow-up rather than done here."*
 *
 * ⛔ **DO NOT REDECLARE IT HERE**, and do not copy the team form's
 * singular-to-plural error fold either: this form has no band control for such
 * an error to point at, so the guard here is the ABSENCE of the key.
 */

/**
 * 🔴 **`billingRegions` IS ABSENT FROM THIS OBJECT AND THAT ABSENCE IS THE
 * GUARD — a personal target is OVERALL and names no band, by the owner's
 * decision.**
 *
 * A TEAM target commits to a figure per billing region — **to SEVERAL bands at
 * once since 2026-09-15** — and `GroupTargetsCard` requires at least one; a
 * PERSONAL target does not, and the two forms share a schema — so "the control
 * is not rendered" would be a rule somebody could undo by adding a control, and
 * `buildPatch` and the create body would follow it silently.
 *
 * Leaving the key off this value object makes a band **inexpressible**: no
 * payload built from these fields can carry one, and a call site that tried
 * would fail the BUILD rather than send a key the server refuses on a `user`
 * subject. This repo's own rule — *a guard someone can forget is not a guard* —
 * and the same shape as `projectId` on the document forms, `ownerId` on quotes,
 * and `travel` on the client-deliverables row: each is absent from the value
 * object rather than merely hidden, because a key left reachable with no control
 * behind it eventually goes out on an unrelated edit.
 *
 * ⚠️ **THE KEY IS STILL SENT ON CREATE, AS `null`, AND THAT IS NOT A HOLE IN
 * THE GUARD.** The create body reaches it through `billingRegionsFor('user',
 * [])`, which defers to `subjectTypeCarriesBillingRegion` — the same one place
 * `GroupTargetsCard` writes its bands through, and it answers `null` for a
 * `user` subject whatever it is handed. So the VALUE is decided by the subject
 * type and not by anything on this screen: what `FormState` makes inexpressible
 * is a band a person could pick, which is the thing that would be wrong.
 *
 * ⛔ **THE PLURAL KEY ONLY, NEVER BOTH.** The server still accepts the singular
 * `billingRegion`, and a body carrying both is a **422 naming both** — so this
 * form sends `billingRegions` and the singular appears in no payload it builds.
 *
 * ⚠️ **A PATCH sends neither key EVER**, because `buildPatch` is a diff over
 * these fields and there is no field to diff — which is the same absence doing
 * the work one layer down.
 *
 * ⚠️ **So NEITHER `billingRegions` NOR `billingRegion` is in `KNOWN_WRITE_KEYS`**,
 * which is what routes a 422 naming either to the BANNER rather than to a field
 * that does not exist. See that constant.
 */
interface FormState {
  metric: TargetMetric;
  /** The `<select>` value: a stringified period id, or '' for none chosen. */
  periodId: string;
  targetAmount: string;
  /** Always held, even for a count — the control is hidden, not the state. */
  baseCurrency: string;
}

function blankForm(): FormState {
  return { metric: 'booked', periodId: '', targetAmount: '', baseCurrency: '' };
}

function recordForm(target: Target): FormState {
  return {
    metric: target.metric,
    periodId: String(target.period.id),
    targetAmount: target.targetAmount,
    baseCurrency: target.baseCurrency ?? '',
  };
}

/**
 * The write keys this form actually renders.
 *
 * Derived from the form's own value object rather than written out, so it
 * cannot drift from the controls — `Object.keys(blankForm())` and the four
 * inputs move together or not at all.
 *
 * `subjectType` and `subjectId` are deliberately ABSENT: they are real write
 * keys, but no control here shows them, so a 422 naming either must go to the
 * banner. Routed to a field it would vanish from the banner, inflate the "one
 * field needs attention" count and highlight nothing — the exact failure
 * `routeFieldErrors` exists to prevent.
 *
 * ⚠️ **`billingRegions` — and the singular `billingRegion` the stored column is
 * still named after — are absent for the same reason and one stronger.** Both
 * are real write keys that this form can never send — see `FormState` — so a 422
 * naming either would be the server refusing a band on a personal target, which
 * is a statement about the REQUEST rather than about anything on screen. It
 * belongs in the banner, verbatim, and marking it would point at a control that
 * does not exist and cannot be made to exist.
 *
 * ⚠️ **`GroupTargetsCard` folds the singular onto the plural and this form must
 * NOT copy that.** There the fold routes a refusal to a control that exists;
 * here there is no control, so the same fold would invent one.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(blankForm());

/** The target list, as one value rather than four correlated booleans. */
type TargetsState =
  | { kind: 'loading' }
  | { kind: 'ready'; targets: Target[]; truncated: boolean }
  /** The audit refusal. Distinct from `error` because the copy must be. */
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

type PeriodsState =
  | { kind: 'loading' }
  /**
   * `periods` is the ACTIVE ones — the only ones this form may offer, because a
   * retired id is a 422 on `periodId`. `retired` is the count of the rest, kept
   * so the empty state can tell "none has been created" apart from "every one
   * has been retired". Server-side filtering would collapse those two.
   */
  | { kind: 'ready'; periods: TargetPeriodRecord[]; retired: number; truncated: boolean }
  | { kind: 'error'; message: string };

/**
 * The currency picker's options, or `null` when the master could not be read.
 *
 * `null` is not an empty list, and the difference is the whole point: an empty
 * list offered as a picker says "there are no currencies", which is never true.
 * The caller falls back to a free-text code instead, because unlike a picklist
 * id a currency code is something a person actually knows.
 */
type CurrencyState = ComboboxOption[] | null;

export function UserTargetsModal({ open, user, currentUserId, onClose }: UserTargetsModalProps) {
  const isSelf = user !== null && user.id === currentUserId;

  const [targets, setTargets] = useState<TargetsState>({ kind: 'loading' });
  const [periods, setPeriods] = useState<PeriodsState>({ kind: 'loading' });
  const [currencies, setCurrencies] = useState<CurrencyState>(null);

  const [form, setForm] = useState<FormState>(blankForm);
  const [baseline, setBaseline] = useState<FormState | null>(null);
  /** The row being edited, or null for the create form. */
  const [editing, setEditing] = useState<Target | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<Target | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const userId = user?.id ?? null;

  /*
   * Reopening — or opening on a different person — must not show the previous
   * record's rows, draft or errors. Done in the render phase rather than in an
   * effect, which would paint the stale dialog for a frame first.
   */
  const resetKey = `${open ? 'open' : 'closed'}|${userId ?? 'none'}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    setForm(blankForm());
    setBaseline(null);
    setEditing(null);
    setErrors({});
    setFormError(null);
    setSaving(false);
    setPendingDelete(null);
    setDeleteError(null);
    /*
     * The two read states are reset HERE rather than at the top of their effect
     * below. Flipping them inside the effect works, but it is a second render
     * pass for a value that is knowable during the first — and it is the pass
     * that paints the PREVIOUS person's periods and currency list for a frame.
     * Same reasoning as `ProductInclusionsModal`, which derives its pending flag
     * for exactly this reason.
     */
    setTargets({ kind: 'loading' });
    setPeriods({ kind: 'loading' });
    setCurrencies(null);
  }

  const loadTargets = useCallback(
    (signal?: AbortSignal) => {
      if (userId === null) return Promise.resolve();
      /*
       * Deliberately does NOT flip to `loading` here. Its first caller is an
       * effect, and a synchronous setState there costs a second render pass for
       * a value already known during the first — the reset block below sets it
       * instead. Its other two callers are a save and a delete, where replacing
       * the list with skeletons for 200ms is worse than leaving the rows up
       * until the new ones arrive.
       */
      return api
        .listTargets(
          {
            subjectType: 'user',
            /*
             * ⚠️ Sent on EVERY read, including the caller's own row, and never
             * dropped on failure. Retrying without it would answer with the
             * caller's own targets under this person's name.
             */
            subjectId: userId,
            perPage: TARGETS_PAGE_SIZE,
            sort: 'periodStart',
            order: 'asc',
          },
          signal,
        )
        .then((result) => {
          if (signal?.aborted) return;
          setTargets({
            kind: 'ready',
            targets: result.data,
            truncated: result.data.length < result.meta.total,
          });
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            setTargets({ kind: 'forbidden' });
            return;
          }
          setTargets({
            kind: 'error',
            message: caught instanceof Error ? caught.message : 'The targets could not be loaded.',
          });
        });
    },
    [userId],
  );

  useEffect(() => {
    if (!open || userId === null) return;
    const controller = new AbortController();
    void loadTargets(controller.signal);
    return () => controller.abort();
  }, [open, userId, loadTargets]);

  /*
   * The windows, and the currency master. Both are read once per opening rather
   * than per person: neither depends on who is being looked at.
   */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const { signal } = controller;

    void api
      /*
       * ⚠️ NO `isActive` FILTER, and split at render instead — the deal-picker
       * rule, for two reasons that both bite here.
       *
       * A retired period is a **422 on `periodId`** at write time, so only the
       * active ones may be OFFERED. But filtering server-side would hand back
       * the identical empty list whether no period has ever been created or
       * every one has been retired, and those are two different problems with
       * two different next steps. One request answers both.
       *
       * `perPage` is this endpoint's ceiling of 200 (its default is 50, which is
       * not the whole list), and `sort`/`order` are left at the server's own
       * `startDate` ascending — the timeline reading.
       */
      .listTargetPeriods({ perPage: PERIODS_PAGE_SIZE }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setPeriods({
          kind: 'ready',
          periods: result.data.filter((period) => period.isActive),
          retired: result.data.filter((period) => !period.isActive).length,
          truncated: result.data.length < result.meta.total,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setPeriods({
          kind: 'error',
          message:
            caught instanceof Error ? caught.message : 'The target periods could not be loaded.',
        });
      });

    void api
      .listCurrencies({ perPage: CURRENCY_PAGE_SIZE, isActive: true }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setCurrencies(
          result.data.map((currency) => ({
            value: currency.currencyCode,
            label: currency.currencyCode,
            hint: currency.currencyName,
          })),
        );
      })
      .catch(() => {
        // Deliberately silent: a failed currency read is not an error about
        // targets, and the control degrades to a typed code rather than
        // disappearing. Saying "we could not load currencies" above a working
        // input would read as a fault the reader has to act on.
        if (!signal.aborted) setCurrencies(null);
      });

    return () => controller.abort();
  }, [open]);

  const isEdit = editing !== null;
  const moneyMetric = metricIsMoney(form.metric);

  /*
   * The currency options with the stored code injected when the master does not
   * list it — the same rule as `withStoredOption()` in `use-reference-options`,
   * and for the same reason: a stored value that is not on offer must still show
   * as selected, or the control silently reports a different currency from the
   * one the row holds.
   */
  const currencyOptions = useMemo<ComboboxOption[]>(() => {
    if (currencies === null) return [];
    const stored = form.baseCurrency.trim().toUpperCase();
    if (stored === '' || currencies.some((option) => option.value === stored)) return currencies;
    return [
      { value: stored, label: stored, hint: 'Not in the currency list — kept as it is' },
      ...currencies,
    ];
  }, [currencies, form.baseCurrency]);

  const periodOptions = periods.kind === 'ready' ? periods.periods : [];
  /*
   * A period the row points at but the list does not carry — a retired or
   * paged-out window. Injected for exactly the reason above: without it an
   * existing target's period control reads as "none chosen", and the next save
   * would move the target to whatever the user picked instead.
   */
  const missingPeriod =
    editing !== null && !periodOptions.some((period) => period.id === editing.period.id)
      ? editing.period
      : null;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key as string];
      return next;
    });
  }

  function startEdit(target: Target) {
    setEditing(target);
    const values = recordForm(target);
    setForm(values);
    setBaseline(values);
    setErrors({});
    setFormError(null);
  }

  function cancelEdit() {
    setEditing(null);
    setForm(blankForm());
    setBaseline(null);
    setErrors({});
    setFormError(null);
  }

  /**
   * The PATCH body: the diff, plus the one pair that cannot be diffed key by key.
   *
   * ⚠️ **`metric` drags `baseCurrency` with it, always.** The server checks the
   * POST-MERGE state, so `{metric: 'won_deals'}` alone is refused for holding a
   * currency the caller never sent — and `{metric: 'booked'}` alone is refused
   * for holding none. Emitting the pair structurally is what makes both
   * directions work; a per-key loop produces a 422 naming a field the user did
   * not touch.
   */
  function buildPatch(next: FormState, previous: FormState): TargetUpdateInput {
    const body: TargetUpdateInput = {};

    if (next.metric !== previous.metric) {
      body.metric = next.metric;
      body.baseCurrency = baseCurrencyFor(next.metric, next.baseCurrency);
    } else {
      const after = baseCurrencyFor(next.metric, next.baseCurrency);
      const before = baseCurrencyFor(previous.metric, previous.baseCurrency);
      if (after !== before) body.baseCurrency = after;
    }

    if (next.periodId !== previous.periodId) body.periodId = Number(next.periodId);

    const amount = next.targetAmount.trim();
    if (amount !== previous.targetAmount.trim()) body.targetAmount = amount;

    return body;
  }

  const patch = baseline === null ? null : buildPatch(form, baseline);
  // Derived from the payload itself, so "Save is enabled" and "there is
  // something to send" cannot disagree — an empty PATCH is a guaranteed 422.
  const dirty = patch === null || Object.keys(patch).length > 0;

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (form.periodId.trim() === '') found.periodId = 'Choose the period this target covers.';

    const amount = form.targetAmount.trim();
    if (amount === '') {
      found.targetAmount = moneyMetric ? 'Enter the target amount.' : 'Enter the number of deals.';
    } else if (!isTargetAmount(amount)) {
      found.targetAmount = 'Enter a positive figure with at most two decimal places.';
    }

    if (moneyMetric) {
      const code = form.baseCurrency.trim().toUpperCase();
      if (code === '') found.baseCurrency = 'Choose the currency this target is set in.';
      else if (!CURRENCY_PATTERN.test(code)) {
        found.baseCurrency = 'A currency is a three-letter code, such as GBP.';
      }
    }

    return found;
  }

  function handleError(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Could not reach the server. Check your connection and try again.');
      return;
    }

    if (caught.status === 403) {
      setErrors({});
      setFormError(
        'The server refused this. Your account does not have permission to set targets, and nothing was saved.',
      );
      return;
    }

    if (caught.status === 404) {
      setErrors({});
      setFormError(
        isEdit
          ? 'This target no longer exists — somebody may have deleted it. Close and reopen this dialog.'
          : 'The server could not find the period or the person this target is for. Close and reopen this dialog.',
      );
      return;
    }

    /*
     * Everything the server said, routed against THIS form's own write-key list
     * rather than against the shape of the path. A path naming a real API key
     * this form does not render — `subjectType`, `subjectId` — is unroutable and
     * belongs in the banner; marked as a field it would highlight nothing while
     * still claiming a field was highlighted.
     */
    const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
    setErrors(fields);

    const lines: string[] = [];
    if (caught.status === 409) {
      /*
       * ⚠️ THE KEY GAINED THE CURRENCY AND THIS SENTENCE HAD TO MOVE WITH IT.
       *
       * It used to read "there is one target per metric per period", which was
       * true of the key migration 086 shipped and is now WRONG: migration 091
       * widened it to `(subject, metric, period, baseCurrency)` on the owner's
       * ruling, so a GBP target and a USD target for one metric and one period
       * are two ordinary rows. The old sentence would send somebody hunting a
       * duplicate that does not exist, and — worse — talk them out of setting a
       * second currency's target, which is the thing the migration was for.
       *
       * `TARGET_ALREADY_EXISTS` is matched rather than the message, because the
       * server's own sentence predates the widened key and still names only
       * three of the four fields. Anything else falls through to it below.
       */
      const code = form.baseCurrency.trim().toUpperCase();
      lines.push(
        caught.code !== TARGET_ALREADY_EXISTS
          ? 'A target already holds that place — check the list above.'
          : moneyMetric && code !== ''
            ? `${targetMetricLabel(form.metric)} is already targeted for that period in ${code} — open that one and edit it. A different currency is fine.`
            : `${targetMetricLabel(form.metric)} is already targeted for that period — open that one and edit it.`,
      );
    }
    for (const message of unroutable) if (!lines.includes(message)) lines.push(message);
    /*
     * ⚠️ Not `formMessage ?? message`. Both halves can be true at once — the
     * generic "Validation failed" and the specific detail — and a coalesce
     * between two messages drops one of them silently.
     */
    if (lines.length === 0 && Object.keys(fields).length === 0) lines.push(caught.message);

    setFormError(lines.length > 0 ? lines.join(' ') : null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving || user === null) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError(null);
    try {
      if (editing !== null && patch !== null) {
        // Belt and braces: Save is disabled in this state, and an empty PATCH is
        // a guaranteed 422 that must never leave the client.
        if (Object.keys(patch).length === 0) return;
        await api.updateTarget(editing.id, patch);
      } else {
        const body: TargetCreateInput = {
          subjectType: 'user',
          subjectId: user.id,
          /*
           * ⚠️ SENT, not omitted, and it is `null` because this form only ever
           * makes a target for a PERSON. `subjectGroupId` (migration 124) is
           * required on a `user_group` target and refused on this one, checked
           * against the POST-MERGE state — so the explicit null is what says
           * "this names nobody's group", rather than leaving the key absent and
           * relying on a stored value that does not exist yet.
           */
          subjectGroupId: null,
          /*
           * ⚠️ **`null`, AND IT IS THE ONLY VALUE THIS FORM CAN PRODUCE.** A
           * personal target is OVERALL — owner's decision — and a band on one is
           * a **422**, checked post-merge beside `subjectType`.
           *
           * ⛔ **THE PLURAL KEY, AND ONLY THE PLURAL KEY.** The server still
           * accepts the singular `billingRegion`, but **sending BOTH in one body
           * is a 422 naming both**, so the singular appears nowhere here.
           *
           * ⚠️ **Through `billingRegionsFor` rather than written as a bare
           * `null`**, for the reason that helper's own doc gives: `subjectType`
           * is written in this same body and the two must travel together, so
           * the decision belongs in the one place that makes it — the same shape
           * as `baseCurrencyFor` three lines down. Reached with an EMPTY set
           * because there is no control here to read bands from, and the helper
           * answers `null` for a `user` subject whatever it is handed.
           *
           * ⚠️ **The KEY is sent; the VALUE cannot vary.** `FormState` holds no
           * bands, so nothing in this form can make this anything else — see
           * that interface for why the absence is the guard rather than the
           * hidden control.
           */
          billingRegions: billingRegionsFor('user', []),
          metric: form.metric,
          periodId: Number(form.periodId),
          // Sent as typed, never re-formatted: rewriting "250000" to
          // "250000.00" would save something other than what was entered, and
          // the server owns the canonical form it stores.
          targetAmount: form.targetAmount.trim(),
          baseCurrency: baseCurrencyFor(form.metric, form.baseCurrency),
        };
        await api.createTarget(body);
      }
      cancelEdit();
      await loadTargets();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (pendingDelete === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteTarget(pendingDelete.id);
      if (editing?.id === pendingDelete.id) cancelEdit();
      setPendingDelete(null);
      await loadTargets();
    } catch (caught) {
      setDeleteError(
        caught instanceof ApiError && caught.status === 403
          ? 'You do not have permission to remove a target. Nothing was deleted.'
          : caught instanceof Error
            ? caught.message
            : 'The target could not be deleted.',
      );
    } finally {
      setDeleting(false);
    }
  }

  const heading = user === null ? 'Targets' : `Targets for ${user.name}`;
  /*
   * Everything about the windows is suppressed once the read was REFUSED. It is
   * all still true, and none of it is the thing the reader has to act on: being
   * told there are no periods, under a notice saying this account may not see
   * these targets at all, offers a next step that is not theirs to take.
   */
  const forbidden = targets.kind === 'forbidden';
  const noPeriods = !forbidden && periods.kind === 'ready' && periods.periods.length === 0;
  /** How many exist but are retired — the discriminator for the copy above. */
  const periodsRetired = periods.kind === 'ready' ? periods.retired : 0;
  /*
   * The form is only offered once there is something to point a target at. Two
   * separate reasons, kept separate: a window list that is empty (nothing to
   * choose) and one that could not be read (we do not know what there is).
   */
  const formUsable = !forbidden && periods.kind === 'ready' && periods.periods.length > 0;

  return (
    <>
      <Modal
        open={open && user !== null}
        onClose={onClose}
        size="lg"
        dismissOnBackdrop={false}
        title={heading}
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div className="grid gap-4">
          <TargetList
            state={targets}
            name={user?.name ?? 'this person'}
            isSelf={isSelf}
            // The SAME value that decides whether the form renders, so the
            // list's copy cannot describe a form that is not there.
            canAdd={formUsable}
            editingId={editing?.id ?? null}
            onEdit={startEdit}
            onDelete={(target) => {
              setDeleteError(null);
              setPendingDelete(target);
            }}
          />

          {!forbidden && periods.kind === 'loading' && <Skeleton className="h-9 w-full" />}

          {!forbidden && periods.kind === 'error' && (
            <Notice tone="danger" role="alert" className="break-words">
              <span className="font-semibold text-fg">The target periods could not be loaded.</span>{' '}
              {periods.message} Nothing can be added or moved until they are, because every target
              names one.
            </Notice>
          )}

          {/*
            ⚠️ The day-one state, and it is NOT "no targets set".
            `f_target_periods` shipped empty, so there is nothing for a target to
            point at. Wording this as an empty target list would invite somebody
            to press an Add button that cannot succeed; wording it as an error
            would send them looking for a fault that is not there.

            ⚠️ TWO CAUSES, TWO SENTENCES, and they are not interchangeable —
            which is the whole reason the periods are fetched unfiltered. "None
            has been created" and "every one has been retired" arrive as the same
            empty picker, and only one of them is fixed by adding a period; the
            other is fixed by restoring one. Telling somebody to add a fifth
            quarter when four already exist retired is how a duplicate window
            gets created and collects a 409.
          */}
          {noPeriods && (
            <EmptyState
              icon={<CalendarIcon />}
              title={
                periodsRetired > 0
                  ? 'Every target period has been retired'
                  : 'No target periods exist yet'
              }
              description={
                periodsRetired > 0
                  ? `All ${periodsRetired} are retired. Restore or add one under Catalog → Target periods.`
                  : 'Add one under Catalog → Target periods.'
              }
            />
          )}

          {formUsable && (
            <form onSubmit={(event) => void handleSubmit(event)} noValidate className="grid gap-3">
              <div className="flex items-baseline justify-between gap-3 border-t border-border pt-4">
                <h3 className="m-0 text-sm font-medium text-fg">
                  {isEdit ? 'Edit this target' : 'Add a target'}
                </h3>
                {isEdit && (
                  <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
                    Cancel edit
                  </Button>
                )}
              </div>

              {formError && (
                <Notice tone="danger" role="alert" className="break-words">
                  {formError}
                </Notice>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Metric" required error={errors.metric}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={form.metric}
                      invalid={Boolean(errors.metric)}
                      onChange={(event) => setField('metric', event.target.value as TargetMetric)}
                    >
                      {TARGET_METRICS.map((metric) => (
                        <option key={metric} value={metric}>
                          {targetMetricLabel(metric)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label="Period" required error={errors.periodId}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={form.periodId}
                      invalid={Boolean(errors.periodId)}
                      onChange={(event) => setField('periodId', event.target.value)}
                    >
                      <option value="">Select a period…</option>
                      {missingPeriod && (
                        <option value={String(missingPeriod.id)}>
                          {describeTargetPeriod(missingPeriod)} — no longer listed
                        </option>
                      )}
                      {periodOptions.map((period) => (
                        <option key={period.id} value={String(period.id)}>
                          {describeTargetPeriod(period)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field
                  label={moneyMetric ? 'Target amount' : 'Target number of deals'}
                  required
                  error={errors.targetAmount}
                >
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={form.targetAmount}
                      inputMode="decimal"
                      invalid={Boolean(errors.targetAmount)}
                      onChange={(event) => setField('targetAmount', event.target.value)}
                    />
                  )}
                </Field>

                {/*
                  ⚠️ HIDDEN, not disabled, for a count — and the state behind it
                  is left alone. `baseCurrencyFor` sends null for a count
                  whatever the box holds, so hiding cannot leak a currency onto
                  a `won_deals` row, and switching back to a money metric
                  restores what was chosen rather than blanking it.
                */}
                {moneyMetric ? (
                  <Field label="Currency" required error={errors.baseCurrency}>
                    {({ id, 'aria-describedby': describedBy }) =>
                      currencies === null ? (
                        <Input
                          id={id}
                          aria-describedby={describedBy}
                          value={form.baseCurrency}
                          maxLength={3}
                          autoCapitalize="characters"
                          invalid={Boolean(errors.baseCurrency)}
                          onChange={(event) =>
                            setField('baseCurrency', event.target.value.toUpperCase())
                          }
                        />
                      ) : (
                        <Combobox
                          id={id}
                          aria-describedby={describedBy}
                          value={form.baseCurrency}
                          options={currencyOptions}
                          placeholder="Select a currency…"
                          searchPlaceholder="Search a code or name…"
                          invalid={Boolean(errors.baseCurrency)}
                          onChange={(value) => setField('baseCurrency', value)}
                        />
                      )
                    }
                  </Field>
                ) : null}
              </div>

              <div className="flex items-center justify-end gap-2">
                {isEdit && !dirty && <p className="mr-auto text-xs text-fg-muted">No changes yet.</p>}
                <Button variant="primary" type="submit" loading={saving} disabled={isEdit && !dirty}>
                  {isEdit ? 'Save changes' : 'Add target'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Remove this target?"
        description={
          pendingDelete === null ? null : (
            <>
              The {targetMetricLabel(pendingDelete.metric).toLowerCase()} target of{' '}
              {formatTarget(pendingDelete)} for {describeTargetPeriod(pendingDelete.period)} will be
              deleted. This can't be undone.
            </>
          )
        }
        confirmLabel="Remove target"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
      />
    </>
  );
}

/**
 * The three states every data surface owes the reader, plus a fourth this one
 * genuinely has: the audit refusal.
 */
function TargetList({
  state,
  name,
  isSelf,
  canAdd,
  editingId,
  onEdit,
  onDelete,
}: {
  state: TargetsState;
  name: string;
  isSelf: boolean;
  /**
   * Whether the Add form is actually rendered beneath this list.
   *
   * ⚠️ Required, with no default, because the empty state's copy POINTS AT that
   * form. With no target period stored the form is suppressed, and "Add one
   * below" then names a control that is not on screen — the reader scrolls,
   * finds nothing, and concludes the page is broken. Measured in production on
   * 2026-08-29: both empty states rendered together and this one said "Add one
   * below" with no form under it.
   *
   * Making it a required prop rather than an optional flag is deliberate: a
   * default would let a future call site inherit the wrong sentence silently.
   */
  canAdd: boolean;
  editingId: number | null;
  onEdit: (target: Target) => void;
  onDelete: (target: Target) => void;
}) {
  if (state.kind === 'loading') {
    return (
      <ul className="m-0 grid list-none gap-2 p-0">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <li key={index} className="grid gap-2 rounded-lg border border-border px-3 py-2.5">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-2.5 w-56" />
          </li>
        ))}
      </ul>
    );
  }

  /*
   * ⚠️ The refusal, rendered as itself.
   *
   * `?subjectId=` answers 403 rather than narrowing, so this is the server
   * declining to say — not an empty result. The one thing that must never happen
   * here is a retry without the key: it would succeed, return the READER's own
   * targets, and present them under this person's name with nothing on screen
   * looking wrong. Saying so explicitly is cheaper than someone rediscovering it.
   */
  if (state.kind === 'forbidden') {
    return (
      <Notice
        tone="warning"
        title={isSelf ? 'Your targets could not be shown.' : `You cannot see ${name}’s targets.`}
      >
        <p className="m-0">
          This account is not permitted to read them. Ask an administrator for access.
        </p>
      </Notice>
    );
  }

  if (state.kind === 'error') {
    return (
      <Notice tone="danger" role="alert" title="The targets could not be loaded.">
        <p className="m-0 [overflow-wrap:anywhere]">{state.message}</p>
      </Notice>
    );
  }

  if (state.targets.length === 0) {
    /*
     * ⚠️ With no period stored there is no Add form beneath this, and the
     * "No target periods exist yet" block says so in full — so this one falls
     * silent rather than repeating it or pointing at a control that is not
     * there. Two empty states stacked, one of them naming a missing form, is
     * what production actually rendered on 2026-08-29.
     */
    if (!canAdd) return null;
    return (
      <EmptyState
        icon={<TargetIcon />}
        title={isSelf ? 'You have no targets set' : `${name} has no targets set`}
        description="Add one below."
      />
    );
  }

  return (
    <div className="grid gap-2">
      {/* The truncation clause that rode on this count — "more exist than are
          listed here" — went in the 2026-09-05 sweep. `state.truncated` is
          still computed and is now reported nowhere. */}
      <p className="m-0 text-xs text-fg-muted">
        {pluralise(state.targets.length, 'target')} set.
      </p>
      <ul className="m-0 grid list-none gap-2 p-0">
        {state.targets.map((target) => (
          <li
            key={target.id}
            className="grid gap-2 rounded-lg border border-border px-3 py-2.5 sm:grid-cols-[1fr_auto] sm:items-center"
          >
            <div className="min-w-0">
              <p className="m-0 text-sm font-medium text-fg">
                {targetMetricLabel(target.metric)} ·{' '}
                <span className="tabular">{formatTarget(target)}</span>
              </p>
              <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                {describeTargetPeriod(target.period)}
              </p>
            </div>
            <div className="flex items-center gap-1.5 sm:justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEdit(target)}
                disabled={editingId === target.id}
              >
                {editingId === target.id ? 'Editing' : 'Edit'}
              </Button>
              <Button variant="danger" size="sm" onClick={() => onDelete(target)}>
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TargetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M3 11h18" />
    </svg>
  );
}
